import { holdTabAwake } from "@services/realtime/holdTabAwake";

// Decides whether this tab should keep a live connection: yes while it is visible, no once
// it has been hidden for the whole grace period, and always while something holds it open
// (unsaved changes, a page lock). People keep ten or more tabs open; letting the background
// ones go on purpose is more reliable than letting the browser throttle them into dropping.
// `onChange` fires whenever the answer flips.
export function createVisibilityGate({ doc, graceMs, onChange }) {
  let holds = 0;
  let paused = false;
  let graceTimer = null;
  let releaseAwake = null;

  const cancelGrace = () => {
    clearTimeout(graceTimer);
    graceTimer = null;
  };

  // Showing the tab and taking a hold both cancel the timer, so when it fires the tab is
  // still hidden and nothing holds it.
  const startGrace = () => {
    if (holds > 0 || doc.visibilityState !== "hidden") return;
    cancelGrace();
    graceTimer = setTimeout(() => {
      graceTimer = null;
      paused = true;
      onChange();
    }, graceMs);
  };

  const reopen = () => {
    if (!paused) return;
    paused = false;
    onChange();
  };

  doc.addEventListener("visibilitychange", () => {
    if (doc.visibilityState === "hidden") {
      startGrace();
      return;
    }
    cancelGrace();
    reopen();
  });
  startGrace();

  const hold = () => {
    holds += 1;
    if (holds === 1) releaseAwake = holdTabAwake();
    cancelGrace();
    reopen();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      holds -= 1;
      if (holds > 0) return;
      releaseAwake();
      releaseAwake = null;
      startGrace();
    };
  };

  return { isOpen: () => !paused, hold };
}
